import React, { useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { clearTicket } from '../state';
import { useGetTicketByIdQuery } from '../../../services/TicketService';
import { PageContainer, PageCard } from '../../common/components';
import { TicketEditHeader } from '../components/TicketForm/headers';
import { TicketEditForm } from '../components/TicketForm';

/**
 * TicketEdit - Main page component for editing existing support tickets
 * Loads ticket data from API using query parameters and provides editing interface
 */
const TicketEdit: React.FC = () => {
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('id');
  const dispatch = useAppDispatch();

  // Query to load the ticket by ID
  const {
    data: ticket,
    isLoading,
    isError,
    error,
  } = useGetTicketByIdQuery(ticketId || '', {
    skip: !ticketId, // Skip query if no ticket ID is provided
    refetchOnMountOrArgChange: true, // Force refetch when ticket ID changes
  });

  useEffect(() => {
    // Clear ticket state immediately when ticket ID changes
    if (ticketId) {
      dispatch(clearTicket());
    }
  }, [dispatch, ticketId]);

  // Show loading state
  if (isLoading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <Box textAlign="center">
            <CircularProgress size={40} sx={{ color: '#3b82f6' }} />
            <Box mt={2} color="#64748b">
              Loading ticket details...
            </Box>
          </Box>
        </Box>
      </PageContainer>
    );
  }

  // Show error state
  if (isError || !ticketId) {
    const errorMessage = !ticketId 
      ? 'No ticket ID provided. Please provide a valid ticket ID in the URL.'
      : 'Failed to load ticket. Please check the ticket ID and try again.';

    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" mt={4}>
          <Alert 
            severity="error" 
            sx={{ 
              maxWidth: 600,
              '& .MuiAlert-message': { fontSize: '1rem' }
            }}
          >
            {errorMessage}
            {isError && error && 'message' in error && (
              <Box mt={1} color="#b91c1c" fontSize="0.875rem">
                Details: {error.message}
              </Box>
            )}
          </Alert>
        </Box>
      </PageContainer>
    );
  }

  // Show main edit interface
  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <TicketEditHeader />
          <TicketEditForm />
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default TicketEdit;
